import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  ApplicationsSummaryCard,
  FinancingBenefits,
  FinancingFooter,
  FinancingHero,
  FinancingPartnersStrip,
  FinancingSteps,
  PageBack,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@hero/web-components';
import { ArrowRightIcon } from '@hero/web-icons';
import { useFeature, usePageContext } from '@hero/web-context';
import { fileToBase64 } from '@hero/web-utils';
import {
  useFinancingApplicationsApi,
  useFinancingCreateApplicationApi,
  useFinancingPartnersListApi,
  useFinancingUploadInvoiceDocumentApi,
  useOrgProfileApi,
  useOrgProfileUpdateApi,
} from '@hero/api-hooks';
import { getObjectId, type ImageWithUpload } from '@hero/schema';
import { profileToFormValues } from './utils/financingProfile';
import { resolvePilotLenderLogoUrl } from './utils/lenderLogo';
import { ApplicationsTable } from './components/ApplicationsTable';
import {
  ApplyForm,
  type ApplyFormHandle,
  type ApplyFormSubmitPayload,
} from './components/ApplyForm';
import {
  applicationToMock,
  termFromMock,
} from './adapters/applicationAdapter';

type TabKey = 'overview' | 'apply' | 'history' | 'learn';

const TAB_KEYS: TabKey[] = ['overview', 'apply', 'history', 'learn'];
const DEFAULT_TAB: TabKey = 'overview';
const RECENT_LIMIT = 4;

/**
 * Read & validate the `?tab=` query param. Anything we don't recognise
 * falls back to `overview` so an external link with a typo never lands
 * the user on a blank page.
 */
const parseTabParam = (value: string | null): TabKey =>
  (TAB_KEYS as readonly string[]).includes(value ?? '')
    ? ((value as TabKey) ?? DEFAULT_TAB)
    : DEFAULT_TAB;

/**
 * Carrier financing landing page.
 *
 * Layout: a 4-tab dashboard (Overview / New application / My
 * applications / How it works) instead of a single long scrolling
 * landing. Each tab is a focused surface so the user only sees what
 * matches their intent:
 *
 *  - **Overview**: compact hero + summary stats + recent
 *    applications + "Learn more" prompt for first-time visitors.
 *  - **New application**: the compact 3-step wizard.
 *  - **My applications**: the full applications table.
 *  - **How it works**: marketing content — hero (full variant),
 *    steps, benefits, partners strip, footer disclosure. All those
 *    blocks come from `@hero/web-components`'s shared
 *    `feature/Financing/*` modules so a standalone public landing
 *    page can be assembled from the same parts.
 *
 * Data wiring is unchanged from the original page — only the
 * presentation has been refactored.
 */
export default function FinancingCarrierPage() {
  const { t } = useTranslation('m-financing');
  const { setPageName } = usePageContext();
  const applyFormRef = useRef<ApplyFormHandle | null>(null);

  /**
   * Tab state is the URL — using `?tab=apply` keeps deep-links,
   * browser back/forward, and "copy link to share with my finance
   * person" all working without any extra state plumbing. The setter
   * preserves any other search params (e.g. future filters).
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTabParam(searchParams.get('tab'));
  const expandApplicationId = searchParams.get('applicationId');
  const setTab = useCallback(
    (next: TabKey) => {
      setSearchParams(
        prev => {
          const sp = new URLSearchParams(prev);
          if (next === DEFAULT_TAB) sp.delete('tab');
          else sp.set('tab', next);
          return sp;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /**
   * Apply wizard sub-flag — when off (e.g. payload status set to
   * `coming_soon` during a brief lender-side incident) we keep
   * the dashboard / history / learn surfaces visible but hide the
   * Apply tab and every "Apply now" CTA so carriers can't submit
   * new applications. The data the parent page already shows still
   * comes through.
   */
  const applyFeature = useFeature('financing_module', 'sub_carrier_apply');
  const canApply = applyFeature.isEnabled;

  const applicationsQuery = useFinancingApplicationsApi({});
  const createApplication = useFinancingCreateApplicationApi();
  const uploadDocument = useFinancingUploadInvoiceDocumentApi();
  const updateOrgProfile = useOrgProfileUpdateApi();
  const partnersQuery = useFinancingPartnersListApi({});
  /**
   * Pre-load the carrier's `Org.financingProfile` for wizard draft
   * pre-fill — sourced from the unified `useOrgProfileApi()`
   * projection (`.financingProfile`). The dedicated
   * `/financing/profile` endpoint is gone.
   */
  const orgProfileQuery = useOrgProfileApi();
  const profileFormDefaults = useMemo(
    () => profileToFormValues(orgProfileQuery.data?.financingProfile ?? null),
    [orgProfileQuery.data],
  );
  /**
   * Map `FinancingPartner` → `LenderDisplay` for the shared
   * `FinancingPartnersStrip`. The strip self-hides when the list
   * is empty so we don't need a loading skeleton. `name` and
   * `tagline` are already localized server-side from
   * `apps/backend/src/i18n/{en,ar}/main.json` based on the
   * request's `Accept-Language` header — render as-is.
   */
  const partnerLenders = useMemo(
    () =>
      partnersQuery.data?.partners.map(p => ({
        id: p.id,
        name: p.name,
        tagline: p.tagline,
        logoUrl: resolvePilotLenderLogoUrl(p.slug, p.logoUrl),
        brandColor: p.brandColor,
        website: p.website,
      })) ?? [],
    [partnersQuery.data],
  );

  useEffect(() => {
    setPageName(t('page.title'));
  }, [setPageName, t]);

  /**
   * Live applications, straight from the API — no demo fallback.
   * `useFinancingApplicationsApi` is an infinite-query hook; its
   * flattened, de-duped list lives on `.data` (see
   * `UseInfinitePaginationReturn` in
   * `libs/frontend/shared/api-hooks/src/types/pagination.ts`). Empty
   * `data` (or an error / still-loading query) yields an empty list,
   * which the dashboard renders as the "no applications yet" empty
   * state below.
   */
  const applications = useMemo(
    () => applicationsQuery.data?.map(applicationToMock) ?? [],
    [applicationsQuery.data],
  );

  const stats = useMemo(() => {
    const total = applications.length;
    const pending = applications.filter(a => a.status === 'submitted').length;
    const approved = applications.filter(
      a => a.status === 'accepted' || a.status === 'funded' || a.status === 'repaid',
    ).length;
    const funded = applications.filter(
      a => a.status === 'funded' || a.status === 'repaid',
    ).length;
    return { total, pending, approved, funded };
  }, [applications]);

  const recentApplications = useMemo(
    () => applications.slice(0, RECENT_LIMIT),
    [applications],
  );

  const goToApply = () => {
    if (!canApply) return;
    if (!searchParams.get('invoiceId')) {
      applyFormRef.current?.resetInvoiceSelection();
    }
    setTab('apply');
    window.setTimeout(() => {
      applyFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleTabChange = useCallback(
    (next: TabKey) => {
      if (next === 'apply' && canApply && !searchParams.get('invoiceId')) {
        applyFormRef.current?.resetInvoiceSelection();
      }
      setTab(next);
    },
    [canApply, searchParams, setTab],
  );

  // Auto-kick out of the Apply tab if the sub-flag flips off mid-session.
  useEffect(() => {
    if (!canApply && tab === 'apply') setTab('overview');
  }, [canApply, tab]);

  /**
   * Bilateral invoice deep link — `navigate('financing', {
   * invoiceId, tab: 'apply' })` from any `<InvoiceDrawer>` "Finance
   * it now" CTA lands here. If the caller forgot to set
   * `tab=apply` (or any other entrypoint links with just
   * `?invoiceId=...`), auto-flip to the Apply tab so the deep
   * link is robust regardless of source. We DO NOT clear the
   * `invoiceId` param here — `ApplyForm` consumes it once and
   * strips it as part of its own deep-link effect.
   */
  useEffect(() => {
    if (!canApply) return;
    const invoiceId = searchParams.get('invoiceId');
    if (invoiceId && tab !== 'apply') {
      setTab('apply');
    }
  }, [searchParams, tab, canApply, setTab]);

  /**
   * Deep link from the invoice drawer's active-financing banner —
   * land on history with the matching row expanded.
   */
  useEffect(() => {
    if (expandApplicationId && tab !== 'history') {
      setTab('history');
    }
  }, [expandApplicationId, tab, setTab]);

  /**
   * Submission spans three sequential mutations (uploads → org
   * profile patch → application create). Relying on each
   * mutation's individual `isPending` flag for the button's
   * disabled state lets it briefly re-enable in the microtask
   * gap between mutations (or whenever a mutation that *isn't*
   * in the OR — e.g. `updateOrgProfile` — is running). One local
   * flag flipped at the start of the handler and reset in
   * `finally` gives us a single continuous "submitting" window
   * that covers the entire flow + any awaits in between.
   */
  const [isSubmittingApplication, setIsSubmittingApplication] = useState(false);

  const handleSubmit = async (payload: ApplyFormSubmitPayload) => {
    setIsSubmittingApplication(true);
    try {
      let documentImage: { id: string; url: string } | undefined;
      if (payload.invoice.source === 'external' && payload.invoice.documentFile) {
        const base64 = await fileToBase64(payload.invoice.documentFile);
        const uploaded = await uploadDocument.mutateAsync({
          image: { upload: base64 },
          clientName: payload.invoice.clientName,
        });
        documentImage = { id: uploaded.id, url: uploaded.url };
      }

      /**
       * Optional financial statements upload. We piggy-back on the
       * invoice-document uploader because the underlying endpoint is
       * generic (upload to Cloudinary, return `{id,url}`). One file
       * max per the schema; multi-file support is a follow-up.
       */
      let financialStatementImages: ImageWithUpload[] | undefined;
      if (payload.financialStatements?.length) {
        const uploads = await Promise.all(
          payload.financialStatements.map(async file => {
            const base64 = await fileToBase64(file);
            const uploaded = await uploadDocument.mutateAsync({
              image: { upload: base64 },
              clientName: payload.invoice.clientName,
            });
            return { id: uploaded.id, url: uploaded.url };
          }),
        );
        financialStatementImages = uploads;
      }

      /**
       * Persist (or refresh) `Org.financingProfile` BEFORE creating
       * the application — the application's `profileSnapshot` is
       * built by the backend from the freshly stored profile. The
       * dedicated `/financing/profile` endpoint is gone; profile
       * updates now travel through the unified `PATCH /org/profile`
       * with a section-keyed body `{ financingProfile: {...} }`.
       */
      await updateOrgProfile.mutateAsync({
        financingProfile: {
          fleetTier: payload.profile.fleetTier,
          annualRevenueTier: payload.profile.annualRevenueTier,
          financingNeeds: payload.profile.financingNeeds,
          ownsAllTrucks: payload.profile.ownsAllTrucks,
          ...(payload.profile.trucksOwnedCount != null && {
            trucksOwnedCount: payload.profile.trucksOwnedCount,
          }),
          hasLoans: payload.profile.hasLoans,
          monthlyFinancingPayment: payload.profile.monthlyFinancingPayment,
          ...(payload.isProfitable != null && {
            isProfitable: payload.isProfitable,
          }),
          ...(financialStatementImages?.length && {
            financialStatements: financialStatementImages,
          }),
        },
      });

      await createApplication.mutateAsync({
        invoice: {
          source: payload.invoice.source,
          ...(payload.invoice.heroInvoice?.id && {
            _id: getObjectId(payload.invoice.heroInvoice.id),
          }),
          clientId: getObjectId(payload.invoice.clientId),
          clientName: payload.invoice.clientName,
          amount: {
            currency: 'SAR',
            total: payload.invoice.amount,
            tax: { total: 0, applied: [] },
            subtotal: payload.invoice.amount,
            itemsSubtotal: payload.invoice.amount,
            realizedSubtotal: payload.invoice.amount,
            itemsOriginalSubtotal: payload.invoice.amount,
            fees: {
              total: 0,
              added: 0,
              subtracted: 0,
              realized: 0,
              all: 0,
              applied: [],
            },
            discount: { amount: 0, percentage: 0 },
            paid: 0,
            remaining: payload.invoice.amount,
          },
          expectedDueDate: new Date(payload.invoice.expectedDueDate),
          document: documentImage,
          invoiceNumber: payload.invoice.invoiceNumber,
        },
        requestedAmount: payload.requestedAmount,
        requestedTerm: termFromMock(payload.requestedTerm),
        /**
         * Lender targeting is left to the backend: when omitted,
         * the service fans the application out to every active
         * pilot lender (network model, no platform picking).
         */
      });

      toast.success(t('apply_form.success_toast'));
      /**
       * Switch to "My Applications" AND reset the wizard step in a
       * SINGLE URL update. Splitting these into two `setSearchParams`
       * calls (one here for `tab`, one inside `ApplyForm` for
       * `apply_step`) causes a race: React Router's `searchParamsRef`
       * isn't updated between back-to-back calls in the same tick, so
       * the second one reads stale prev and clobbers the first —
       * leaving the user stuck on `?tab=apply`. The form keeps its
       * internal state reset (`values` / `completed`) but no longer
       * touches the URL after submit.
       */
      setSearchParams(
        prev => {
          const sp = new URLSearchParams(prev);
          sp.set('tab', 'history');
          sp.delete('apply_step');
          return sp;
        },
        { replace: true },
      );
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t('apply_form.errors.submit_failed'),
      );
      // Re-throw so the wizard knows submission failed and keeps the
      // user's filled values intact (vs. resetting to INITIAL_VALUES).
      throw err;
    } finally {
      setIsSubmittingApplication(false);
    }
  };

  return (
    <>
    <div className="mt-4 space-y-6 pb-12">
      <PageBack redirect="dashboard" />

      <Tabs value={tab} onValueChange={v => handleTabChange(v as TabKey)} className="space-y-6">
        <TabsList className="flex w-full justify-start gap-1 overflow-x-auto rounded-xl bg-gray-100/80 p-1">
          <TabsTrigger value="overview" className="px-4">
            {t('tabs.overview')}
          </TabsTrigger>
          {canApply && (
            <TabsTrigger value="apply" className="px-4">
              {t('tabs.apply')}
            </TabsTrigger>
          )}
          <TabsTrigger value="history" className="px-4">
            {t('tabs.history')}
          </TabsTrigger>
          <TabsTrigger value="learn" className="px-4">
            {t('tabs.learn')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {canApply && <FinancingHero variant="compact" onApply={goToApply} />}

          <ApplicationsSummaryCard
            total={stats.total}
            pending={stats.pending}
            approved={stats.approved}
            funded={stats.funded}
            onApply={canApply ? goToApply : () => undefined}
            onViewApplications={() => setTab('history')}
            disableApply={!canApply}
          />

          {recentApplications.length > 0 ? (
            <section>
              <div className="mb-3 flex items-baseline justify-between">
                <h2 className="text-base font-semibold text-gray-900 sm:text-lg">
                  {t('dashboard.recent.title')}
                </h2>
                <button
                  type="button"
                  onClick={() => setTab('history')}
                  className="text-sm font-medium text-emerald-700 hover:text-emerald-800">
                  {t('dashboard.recent.see_all')}
                </button>
              </div>
              <ApplicationsTable applications={recentApplications} hideHeader expandApplicationId={expandApplicationId} />
            </section>
          ) : (
            <section className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/30 p-6 text-center">
              <h3 className="text-base font-semibold text-emerald-950">
                {t('dashboard.empty.title')}
              </h3>
              <p className="mt-1 text-sm text-emerald-900/80">
                {t('dashboard.empty.description')}
              </p>
              {canApply && (
                <button
                  type="button"
                  onClick={goToApply}
                  className="mt-4 inline-flex items-center rounded-md bg-emerald-700 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-800">
                  {t('dashboard.empty.cta')}
                  <ArrowRightIcon className="ms-2 size-4 rtl:rotate-180" />
                </button>
              )}
            </section>
          )}

          <section className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                {t('dashboard.learn_more.title')}
              </h3>
              <p className="mt-0.5 text-sm text-gray-600">
                {t('dashboard.learn_more.description')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setTab('learn')}
              className="inline-flex items-center rounded-md border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              {t('dashboard.learn_more.cta')}
              <ArrowRightIcon className="ms-2 size-4 rtl:rotate-180" />
            </button>
          </section>
        </TabsContent>

        {canApply && (
          <TabsContent value="apply">
            <ApplyForm
              ref={applyFormRef}
              onSubmit={handleSubmit}
              isSubmitting={
                isSubmittingApplication ||
                uploadDocument.isPending ||
                updateOrgProfile.isPending ||
                createApplication.isPending
              }
              initialValues={profileFormDefaults}
            />
          </TabsContent>
        )}

        <TabsContent value="history" className="space-y-4">
          <ApplicationsTable
            applications={applications}
            expandApplicationId={expandApplicationId}
          />
        </TabsContent>

        <TabsContent value="learn" className="space-y-8">
          <FinancingHero
            onApply={canApply ? goToApply : () => setTab('history')}
            hideCta={!canApply}
          />
          <FinancingPartnersStrip lenders={partnerLenders} />
          <FinancingSteps />
          <FinancingBenefits />
          <FinancingFooter />
        </TabsContent>
      </Tabs>
    </div>
    </>
  );
}
